import { useCallback, useContext } from "react";

import { Clock } from "@magnetic/icons";
import { Select } from "@magnetic/select";
import { SelectOption } from "@magnetic/select/dist/types/types";
import { Text } from "@magnetic/text";
import { ViewSwitcher, ViewSwitcherOption } from "@magnetic/view-switcher";

import { HFlex } from "src/components";
import { TimeZoneContext } from "src/core/timeZoneProvider";
import { TimeFormat } from "src/gen/schema/models/auth";
import { getUserTimeZone, UTC } from "src/utils/timeZone/timeZoneUtils";

import "./timeZoneSection.scss";

const localTimeZone = getUserTimeZone();
const timeZoneOptions: SelectOption[] =
  localTimeZone === UTC
    ? [
        {
          label: "UTC (Local)",
          value: UTC
        }
      ]
    : [
        {
          label: `Local ${localTimeZone}`,
          value: localTimeZone
        },
        {
          label: "UTC",
          value: UTC
        }
      ];
const TIME_FORMAT_SWITCHER_OPTIONS: readonly ViewSwitcherOption[] = [
  { label: "12hr", value: TimeFormat.TIME_FORMAT_12HR.toString() },
  { label: "24hr", value: TimeFormat.TIME_FORMAT_24HR.toString() }
];

export const TimeZoneSection = () => {
  const { timeZone, hourFormat, changeTimeZone, changeTimeFormat } =
    useContext(TimeZoneContext);

  const isTwentyFourHourFormat =
    timeZone === UTC || hourFormat === TimeFormat.TIME_FORMAT_24HR;
  const activeTimeFormat = isTwentyFourHourFormat
    ? TimeFormat.TIME_FORMAT_24HR
    : TimeFormat.TIME_FORMAT_12HR;

  const handleTimeZoneChange = useCallback(
    (value: string) => {
      if (value === UTC) {
        changeTimeFormat(TimeFormat.TIME_FORMAT_24HR);
      }
      changeTimeZone(value);
    },
    [changeTimeFormat, changeTimeZone]
  );

  const handleTimeFormatChange = useCallback(
    (timeFormatValue: string) => {
      if (timeZone === UTC) return;

      changeTimeFormat(Number(timeFormatValue) as TimeFormat);
    },
    [changeTimeFormat, timeZone]
  );

  return (
    <div className="time-zone-format-section">
      <HFlex className="time-zone-section">
        <Clock weight="bold" size={24} className="clock-icon" />
        <Text weight="bold">Timezone</Text>
        <Select
          data-qa="timezone-select"
          placeholder={"Select timezone"}
          options={timeZoneOptions}
          value={timeZone}
          onChange={handleTimeZoneChange}
          size="sm"
          className="timezone-select"
          rootId="timezone-select"
        />
      </HFlex>
      <HFlex justify={"flex-end"} className="hour-cycle-toggle-section">
        <ViewSwitcher
          aria-label="Hour cycle"
          data-qa="hour-cycle-switcher"
          onChange={handleTimeFormatChange}
          options={TIME_FORMAT_SWITCHER_OPTIONS}
          size="sm"
          disabled={timeZone === UTC}
          value={activeTimeFormat.toString()}
        />
      </HFlex>
    </div>
  );
};
